import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { History, Search, RefreshCw, Upload, Loader2, ShieldAlert } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AuditLogList } from './AuditLogList';
import { PaginationFooter } from './PaginationFooter';
import { getAuditActionLabel } from '../../utils/auditActions';
import { formatUserDateTimeWithUTC, formatUTCStamp } from '../../utils/dateFormat';
import { getFileTimestamp } from '../../utils/timezone';
import { csvCell } from '../../utils/csv';
import { toast } from 'sonner';

const SAFE_ACTIONS = [
  'user_login', 'user_login_failed', 'user_logout',
  'session_expired', 'session_ended_inactive', 'session_resumed',
  'password_changed', 'user_impersonated', 'impersonation_ended',
  'user_created', 'user_updated', 'user_blocked', 'user_unblocked',
  'invite_sent', 'invite_accepted',
  'geofence_created', 'geofence_updated', 'geofence_deleted',
  'command_sent', 'report_exported',
  'driver_binding_confirmed', 'driver_binding_responded',
  'trusted_device_removed', 'trusted_network_removed', 'sessions_purged',
];

const TIME_RANGES = [
  { id: 'all', label: 'All Time' },
  { id: '24h', label: 'Last 24 hours' },
  { id: '7d', label: 'Last 7 days' },
  { id: '30d', label: 'Last 30 days' },
];

const memberKindLabel = (m) =>
  m.kind === 'self' ? 'You' : m.kind === 'sub_account' ? 'Sub Account' : (m.person_type ? m.person_type.charAt(0).toUpperCase() + m.person_type.slice(1) : 'Person');

export const ActivityTrailTab = () => {
  const { api, user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [members, setMembers] = useState([]);
  const [memberFilter, setMemberFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [timeRange, setTimeRange] = useState('all');
  const [suspiciousOnly, setSuspiciousOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [fmt, setFmt] = useState({ date: 'DD/MM/YYYY', time: '24h', tz: undefined });
  const fetchSeqRef = useRef(0);

  useEffect(() => {
    api.get('/settings/general')
      .then((res) => setFmt({ date: res.data.date_format, time: res.data.time_format, tz: res.data.timezone }))
      .catch(() => {});
    api.get('/activity-trail/members')
      .then((res) => setMembers(res.data.members || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const buildParams = useCallback((skip, limit) => {
    const params = new URLSearchParams();
    params.set('skip', String(skip));
    params.set('limit', String(limit));
    if (memberFilter !== 'all') params.set('user_id', memberFilter);
    if (actionFilter !== 'all') params.set('action', actionFilter);
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (suspiciousOnly) params.set('suspicious_only', 'true');
    if (timeRange !== 'all') {
      const hours = timeRange === '24h' ? 24 : timeRange === '7d' ? 24 * 7 : 24 * 30;
      params.set('from_date', new Date(Date.now() - hours * 3600 * 1000).toISOString());
    }
    return params;
  }, [memberFilter, actionFilter, debouncedSearch, timeRange, suspiciousOnly]);

  const fetchLogs = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    try {
      const res = await api.get(`/activity-trail?${buildParams((page - 1) * pageSize, pageSize).toString()}`);
      if (seq !== fetchSeqRef.current) return; // stale response guard
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      if (seq === fetchSeqRef.current) {
        setLogs([]);
        setTotal(0);
        if (e?.response?.status !== 403) toast.error('Failed to load activity trail');
      }
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [api, buildParams, page, pageSize]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { setPage(1); }, [memberFilter, actionFilter, debouncedSearch, timeRange, pageSize, suspiciousOnly]);

  const fmtTs = useCallback(
    (ts) => formatUserDateTimeWithUTC(ts, fmt.date, fmt.time, fmt.tz),
    [fmt]
  );

  const memberMap = useMemo(() => Object.fromEntries(members.map((m) => [m.id, m])), [members]);
  const getUserLabel = useCallback((log) => {
    if (user?.role !== 'end_user') return null;
    const m = memberMap[log.user_id];
    if (!m) return log.user_name ? `Account: ${log.user_name}` : null;
    return m.kind === 'self' ? 'Account: You' : `${memberKindLabel(m)}: ${m.name}`;
  }, [memberMap, user?.role]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/activity-trail?${buildParams(0, 1000).toString()}`);
      const rows = res.data.logs || [];
      if (!rows.length) { toast.info('No entries to export'); return; }
      const header = ['Time', 'Time (UTC)', 'Action', 'Account', 'Performed By', 'IP Address', 'Details'];
      const csv = [header.join(',')].concat(rows.map((l) => [
        csvCell(fmtTs(l.timestamp || l.created_at)),
        csvCell(formatUTCStamp(l.timestamp || l.created_at)),
        csvCell(getAuditActionLabel(l.action)),
        csvCell(l.user_name || l.user_email || ''),
        csvCell(l.actor_email || 'System'),
        csvCell(l.ip_address || ''),
        csvCell(l.details || ''),
      ].join(','))).join('\n');
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity_trail_${getFileTimestamp()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      if (res.data.total > rows.length) {
        toast.warning(`Exported the most recent ${rows.length} of ${res.data.total} entries. Narrow the date range to export older entries.`);
      }
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card data-testid="activity-trail-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <History className="w-5 h-5 text-foreground" />
          <CardTitle className="text-base">Activity Trail</CardTitle>
        </div>
        <CardDescription>
          {user?.role === 'end_user'
            ? 'Account activity for you, your Sub Accounts and your People logins. Each entry shows your local time and the exact UTC moment.'
            : 'Your own account activity. Each entry shows your local time and the exact UTC moment.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search action, details, IP..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9"
              data-testid="activity-trail-search"
            />
          </div>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-[180px] h-9" data-testid="activity-trail-action-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              {SAFE_ACTIONS.map((a) => (
                <SelectItem key={a} value={a}>{getAuditActionLabel(a)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {user?.role === 'end_user' && members.length > 1 && (
            <Select value={memberFilter} onValueChange={setMemberFilter}>
              <SelectTrigger className="w-[260px] h-9" data-testid="activity-trail-member-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Accounts</SelectItem>
                {members.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.kind === 'self' ? `You (${m.name})` : `${m.name} — ${memberKindLabel(m)}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[150px] h-9" data-testid="activity-trail-time-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIME_RANGES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            variant={suspiciousOnly ? 'default' : 'outline'}
            size="sm"
            className={`h-9 gap-1.5 ${suspiciousOnly ? 'bg-red-600 hover:bg-red-700 text-white' : ''}`}
            onClick={() => setSuspiciousOnly((v) => !v)}
            aria-pressed={suspiciousOnly}
            title="Show only logins flagged as suspicious (new IP or new device)"
            data-testid="activity-trail-suspicious-filter"
          >
            <ShieldAlert className="w-4 h-4" />
            Suspicious
          </Button>
          <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={fetchLogs} disabled={loading} data-testid="activity-trail-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={handleExport} disabled={exporting || total === 0} data-testid="activity-trail-export">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Export
          </Button>
        </div>
        <p className="text-xs text-muted-foreground" data-testid="activity-trail-total">{total.toLocaleString()} entries</p>
        <AuditLogList
          logs={logs}
          loading={loading}
          formatTimestamp={fmtTs}
          getUserLabel={getUserLabel}
          emptyText="No activity found"
          emptySubText="Account activity will be recorded here"
          heightClass="max-h-[560px]"
        />
        <PaginationFooter
          page={page}
          setPage={setPage}
          totalItems={total}
          itemsPerPage={pageSize}
          setItemsPerPage={setPageSize}
          testIdPrefix="activity-trail"
          disabled={loading}
        />
      </CardContent>
    </Card>
  );
};

export default ActivityTrailTab;
